import type { ProjectOpeningCamera } from '@features/design/helpers/map-camera-handoff.helpers';
import { create } from 'zustand';

/** Shared opening lifecycle; the live list stays mounted until Design is revealed. */
export const useProjectOpening = create<{
  phase: 'idle' | 'loading' | 'rendering' | 'revealing';
  projectId?: number;
  error?: string;
}>(() => ({ phase: 'idle' }));

let prepareMap: ((projectId: number) => Promise<void>) | undefined;
let rendered: (() => void) | undefined;

/** Register the live list camera's full-screen framing operation. */
export const registerProjectOpeningMap = (
  prepare: (projectId: number) => Promise<void>,
): (() => void) => {
  prepareMap = prepare;
  return () => {
    if (prepareMap === prepare) prepareMap = undefined;
  };
};

export const prepareProjectOpeningMap = (projectId: number): Promise<void> =>
  prepareMap?.(projectId) ?? Promise.resolve();

/** Resolve only when the opening project's Design map has actually rendered. */
export const notifyProjectOpeningRendered = (projectId: number): void => {
  if (useProjectOpening.getState().projectId === projectId) rendered?.();
};

export const waitForProjectOpeningRender = (): {
  ready: Promise<void>;
  dispose: () => void;
} => {
  let timer: ReturnType<typeof setTimeout>;
  const ready = new Promise<void>((resolve, reject) => {
    rendered = resolve;
    timer = setTimeout(
      () => reject(new Error('Design map did not render')),
      30000,
    );
  });
  return {
    ready,
    dispose: () => {
      clearTimeout(timer);
      rendered = undefined;
    },
  };
};

/** Reverse transition: hold the centered camera until the list has rendered. */
export const useProjectClosing = create<{
  phase: 'idle' | 'preparing' | 'restoring';
  showLoading: boolean;
  projectId?: number;
  camera?: ProjectOpeningCamera;
}>(() => ({ phase: 'idle', showLoading: false }));

let closingListRendered: (() => void) | undefined;

/** Notify only after the returning list map's tiles and geometry have painted. */
export const notifyProjectClosingListRendered = (): void => {
  closingListRendered?.();
};

/** Install before navigation so even an immediately rendered list can resolve. */
export const waitForProjectClosingListRender = (): {
  ready: Promise<void>;
  dispose: () => void;
} => {
  let timer: ReturnType<typeof setTimeout>;
  const ready = new Promise<void>((resolve) => {
    closingListRendered = resolve;
    timer = setTimeout(resolve, 10000);
  });
  return {
    ready,
    dispose: () => {
      clearTimeout(timer);
      closingListRendered = undefined;
    },
  };
};
